import { useRef, useState } from "react";

import {
  Alert,
  Button,
  Modal,
  TextFieldInput,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { toSentenceCase } from "@smart/itops-utils-basic";

import { buildAddedField, buildAvailableFields } from "./add-field-helpers";
import { AddFieldSection } from "./add-field-section";
import { CustomiseFieldList } from "./customise-field-list";
import {
  areContactDetailsFieldSetsEqual,
  buildStandardNewFields,
  commitAllFieldLabels,
  commitFieldLabel,
  duplicateNameMessage,
  fieldCountLabel,
  isDuplicateName,
  removeField,
  reorderFields,
  CustomisedContactDetailsField,
  setFieldLabel,
  toSavedContactDetailsFields,
} from "./customise-helpers";
import { SaveFieldSetModal } from "./save-field-set-modal";
import { GqlMatterLayout } from "../../../types";
import { fieldComponentIcons } from "../../constants";
import { MatterField, NewFieldOptions } from "../../types";
import { ContactType } from "../../utils";

export type CustomisedContactDetailsInput = {
  uri?: string;
  name: string;
  roleName: string;
  contactType: ContactType;
  fields: CustomisedContactDetailsField[];
};

export type CustomiseContactDetailsModalProps = {
  layout: GqlMatterLayout;
  matterFields: MatterField[];
  contactType: ContactType;
  initialFields?: NewFieldOptions[];
  isSaving: boolean;
  isSavingFieldSet?: boolean;
  saveFieldSetError?: string;
  editingFieldSet?: { uri: string; name: string };
  savedFieldSets?: { uri: string; name: string }[];
  onCancel: () => void;
  onConfirm: (layout: GqlMatterLayout, fields: NewFieldOptions[]) => void;
  onSaveFieldSet?: (input: CustomisedContactDetailsInput) => Promise<void>;
};

export const CustomiseContactDetailsModal = ({
  layout,
  matterFields,
  contactType,
  initialFields,
  isSaving,
  isSavingFieldSet,
  saveFieldSetError,
  editingFieldSet,
  savedFieldSets = [],
  onCancel,
  onConfirm,
  onSaveFieldSet,
}: CustomiseContactDetailsModalProps) => {
  const [draft, setDraft] = useState<NewFieldOptions[]>(
    () =>
      initialFields ||
      buildStandardNewFields({ layout, matterFields, contactType }),
  );
  const originalLabels = useRef<Record<string, string>>(
    Object.fromEntries(draft.map((field) => [field.uri, field.label || ""])),
  );
  const [isAddingField, setIsAddingField] = useState(false);
  const initialFieldSet = useRef(toSavedContactDetailsFields(draft));
  const [savingFieldSetOpen, setSavingFieldSetOpen] = useState(false);
  const [fieldSetName, setFieldSetName] = useState(
    () => editingFieldSet?.name || "",
  );
  const [nameError, setNameError] = useState<string>();

  const roleName = layout.displayName || layout.name || "";
  const hasFieldSetChanges = !areContactDetailsFieldSetsEqual(
    initialFieldSet.current,
    toSavedContactDetailsFields(draft),
  );
  const trimmedName = fieldSetName.trim();
  const hasNameChange =
    !!editingFieldSet && trimmedName !== editingFieldSet.name.trim();
  const canSaveChanges =
    !!draft.length && !!trimmedName && (hasFieldSetChanges || hasNameChange);

  const availableFields = buildAvailableFields({
    layout,
    matterFields,
    draft,
    contactType,
  });

  const addField = (name: string) => {
    const row = availableFields.find((available) => available.name === name);
    if (!row) return;

    const added = buildAddedField({
      layout,
      matterField: row.matterField,
      contactType,
    });
    if (!added) return;

    originalLabels.current[added.uri] = added.label || "";

    setDraft((previous) => [...previous, added]);
  };

  const onSaveChanges = () => {
    if (!editingFieldSet || !onSaveFieldSet) return;

    if (
      isDuplicateName({
        trimmed: trimmedName,
        initialName: editingFieldSet.name,
        existingNames: savedFieldSets
          .filter((fieldSet) => fieldSet.uri !== editingFieldSet.uri)
          .map((fieldSet) => fieldSet.name),
      })
    ) {
      setNameError(duplicateNameMessage);
      return;
    }

    // parent already surfaces errors via saveFieldSetError
    // empty catch only avoids an unhandled rejection
    onSaveFieldSet({
      uri: editingFieldSet.uri,
      name: trimmedName,
      roleName: layout.name,
      contactType,
      fields: toSavedContactDetailsFields(
        commitAllFieldLabels(draft, originalLabels.current),
      ),
    }).catch(() => {});
  };

  return (
    <>
      <Modal
        className="[&_.modal-container]:min-h-[min(90rem,95vh)]"
        open
        onClose={isSavingFieldSet ? undefined : onCancel}
        loading={isSaving || isSavingFieldSet}
        header={{
          icon: fieldComponentIcons.contactDetails.name,
          iconBgColor: fieldComponentIcons.contactDetails.bgColor,
          text: `Customise ${roleName} details`,
        }}
        subHeader={`${toSentenceCase(
          contactType,
        )} · drag to reorder, rename, or remove fields`}
        footer={{
          start:
            onSaveFieldSet && !editingFieldSet ? (
              <Tooltip
                variant="paragraphy"
                placement="top"
                title="Save for future use"
                text="Save this set of fields as a reusable field set for future intake forms."
              >
                <Button
                  text="Save for future use"
                  leftIcon="regularBookmark"
                  variant="secondaryDefault"
                  disabled={
                    isSaving ||
                    isSavingFieldSet ||
                    !draft.length ||
                    !hasFieldSetChanges
                  }
                  onClick={() => setSavingFieldSetOpen(true)}
                />
              </Tooltip>
            ) : undefined,
          buttons: [
            {
              text: "Cancel",
              variant: "secondarySubtle",
              disabled: isSavingFieldSet,
              onClick: onCancel,
            },
            editingFieldSet
              ? {
                  key: "saveFieldSetChanges",
                  text: "Save changes",
                  variant: "primaryDefault",
                  disabled: isSaving || isSavingFieldSet || !canSaveChanges,
                  onClick: onSaveChanges,
                }
              : {
                  key: "addContactDetails",
                  text: fieldCountLabel(draft.length),
                  variant: "primaryDefault",
                  disabled: isSaving || isSavingFieldSet || !draft.length,
                  onClick: () =>
                    onConfirm(
                      layout,
                      commitAllFieldLabels(draft, originalLabels.current),
                    ),
                },
          ],
        }}
      >
        {editingFieldSet && (
          <div>
            <TextFieldInput
              id="field-set-name"
              label="Field set name"
              mandatory
              value={fieldSetName}
              onChange={(e) => {
                setNameError(undefined);
                setFieldSetName(e.target.value);
              }}
              disabled={isSaving || isSavingFieldSet}
              error={!!nameError}
              message={nameError}
            />
          </div>
        )}
        {editingFieldSet && saveFieldSetError && (
          <Alert
            variant="section"
            palette="destructive"
            content={saveFieldSetError}
          />
        )}
        <div>
          <div className="flex items-center gap-1 mb-[0.5rem]">
            <div className="text-ui font-semibold">Fields</div>
          </div>
          <CustomiseFieldList
            fields={draft}
            onReorder={(activeId, overId) =>
              setDraft((previous) => reorderFields(previous, activeId, overId))
            }
            onEditLabel={(uri, label) =>
              setDraft((previous) => setFieldLabel(previous, uri, label))
            }
            onCommitLabel={(uri) =>
              setDraft((previous) =>
                commitFieldLabel(previous, uri, originalLabels.current[uri]),
              )
            }
            onRemove={(uri) =>
              setDraft((previous) => removeField(previous, uri))
            }
          />
        </div>
        <Button
          className="self-start"
          text="Add a field"
          leftIcon="regularPlus"
          variant="primarySubtle"
          size="small"
          disabled={isSaving || isSavingFieldSet || !availableFields.length}
          onClick={() => setIsAddingField(true)}
        />
        {isAddingField && (
          <AddFieldSection
            rows={availableFields}
            onAdd={addField}
            onDismiss={() => setIsAddingField(false)}
          />
        )}
      </Modal>
      {savingFieldSetOpen && onSaveFieldSet && (
        <SaveFieldSetModal
          roleDisplayName={roleName}
          existingNames={savedFieldSets.map((fieldSet) => fieldSet.name)}
          headerText="Save as field set"
          loading={isSavingFieldSet}
          error={saveFieldSetError}
          onCancel={() => {
            if (!isSavingFieldSet) setSavingFieldSetOpen(false);
          }}
          onSave={async (name) => {
            const committed = commitAllFieldLabels(
              draft,
              originalLabels.current,
            );
            await onSaveFieldSet({
              name,
              roleName: layout.name,
              contactType,
              fields: toSavedContactDetailsFields(committed),
            });
          }}
        />
      )}
    </>
  );
};
